import request from './request'
import { isCouponApiSuccess } from './couponApi'

/** 与头像上传相同：axios + multipart/form-data */
const MULTIPART_HEADERS = {
  headers: {
    'Content-Type': 'multipart/form-data' as const,
  },
  timeout: 60000,
}

interface TableUploadResponse {
  code: number
  msg: string
  data: {
    path?: string
    url?: string
    file?: string
    category?: string
    [key: string]: unknown
  }
}

const UPLOAD_PATHS = ['/index.php/api/admin/upload', '/api/admin/upload'] as const

function isImageUploadError(message: string): boolean {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('image extension') ||
    normalized.includes('image type') ||
    normalized.includes('only image')
  )
}

/** Apifox 返回 path：uploads/admin/coupon/...（无前导 /） */
export function formatSpecifiedUsersFilePathForApi(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return trimmed.replace(/^\/+/, '')
}

function readUploadedTablePath(data: TableUploadResponse['data'] | null | undefined): string {
  if (!data) return ''

  const rawPath = typeof data.path === 'string' ? data.path.trim() : ''
  if (rawPath) return formatSpecifiedUsersFilePathForApi(rawPath)

  const rawUrl = typeof data.url === 'string' ? data.url.trim() : ''
  if (!rawUrl) return ''

  let pathname = rawUrl
  if (/^https?:\/\//i.test(rawUrl)) {
    try {
      pathname = new URL(rawUrl).pathname
    } catch {
      pathname = rawUrl
    }
  }
  if (pathname.startsWith('/storage/')) {
    pathname = pathname.slice('/storage'.length)
  }
  return formatSpecifiedUsersFilePathForApi(pathname)
}

function isTableUploadSuccess(response: TableUploadResponse): boolean {
  const msg = (response.msg ?? '').trim().toLowerCase()
  if (isImageUploadError(msg)) return false
  if (readUploadedTablePath(response.data)) return true
  if (
    isCouponApiSuccess(response.code) &&
    (msg.includes('uploaded successfully') || msg.includes('table uploaded'))
  ) {
    return true
  }
  return msg === 'successfully.'
}

/** Apifox：form-data 仅 file + categories 留空 */
function buildApifoxUploadFormData(file: File): FormData {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('categories', '')
  return formData
}

/**
 * 优惠券可见用户表上传 — 与 Apifox / 头像上传相同 axios 方式。
 * POST /index.php/api/admin/upload → data.path
 */
export async function uploadCouponSpecifiedUsersTable(file: File): Promise<string> {
  let lastError = 'Failed to upload table'

  for (const path of UPLOAD_PATHS) {
    const response = await request.post<TableUploadResponse>(
      path,
      buildApifoxUploadFormData(file),
      MULTIPART_HEADERS,
    )

    if (!isTableUploadSuccess(response)) {
      lastError = response.msg || 'Failed to upload table'
      continue
    }

    const filePath = readUploadedTablePath(response.data)
    if (!filePath) {
      lastError = 'Upload succeeded but no file path was returned'
      continue
    }

    return filePath
  }

  throw new Error(lastError)
}
